import { useContext, useEffect } from "react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import {
  BotFormType,
  Error,
  Steps,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";
import { hasMultiValues } from "@shared/features/bots/BotForm/common/utils";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { BotType, ConfigurationItem } from "@shared/types";
import { getParsedTrailingStopFromConfiguration } from "@shared/features/bots/utils";
import { isSymbol } from "@shared/types/utils";
import {
  GENERAL_MAX_VALUE,
  SKIP_SIGNALS_WITHOUT_STOP_TITLE,
  STOP_TIMEOUT_TITLE,
} from "@shared/features/bots/BotForm/common/constants";
import useFormHelperData from "@shared/features/bots/BotForm/common/hooks/useFormHelperData";
import { useGetSecondsTimeoutDisplay } from "@shared/features/bots/hooks";

const useStop = (modifiedConfiguration?: ConfigurationItem) => {
  const {
    botType,
    botGeneralData,
    setTrailingStopData,
    stopTimeout,
    setStopTimeout,
    numberOfContinuousStopsTillBotStop,
    setNumberOfContinuousStopsTillBotStop,
    tabToRestoreChangesData,
    trailingStopData,
    initialized,
    botFormType,
    isMarginExchange,
    addError,
    removeError,
    isChannelConfiguration,
    defaultStop,
    toggleField,
    checkedFields,
    isBacktesting,
  } = useContext(BotFormContext)!;
  const { getHelperData } = useFormHelperData();
  const isBulkEdit = botFormType === BotFormType.BULK_EDIT;
  const isEdit = botFormType === BotFormType.EDIT;
  const isGridBot = botType === BotType.GRID;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isLeveraged = isSignalsBot && isMarginExchange;
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { getSecondsTimeoutDisplay } = useGetSecondsTimeoutDisplay();

  const getDefaultTrailingStop = () => {
    return getParsedTrailingStopFromConfiguration(modifiedConfiguration);
  };

  const initializeTrailingStopData = () => {
    if (modifiedConfiguration && Object.keys(modifiedConfiguration).length) {
      setTrailingStopData(getDefaultTrailingStop());
    }
  };

  useEffect(() => {
    if (
      !isGridBot &&
      !hasMultiValues(trailingStopData) &&
      ((!isBulkEdit && !isEdit && !isBacktesting) || initialized)
    ) {
      initializeTrailingStopData();
    }
  }, [initialized, modifiedConfiguration]);

  const stopTimeoutOptions = botGeneralData?.options["Stop Timeout"]?.[0];
  const stopTimeoutIsMultiValue = isSymbol(stopTimeout);

  const stopTimeoutSelectorData = {
    shouldDisplay: !isTradingViewSignalsBot,
    value: stopTimeoutIsMultiValue ? undefined : stopTimeout,
    onChange: setStopTimeout,
    options:
      stopTimeoutOptions?.map((option) => ({
        value: option.value,
        name: getSecondsTimeoutDisplay(option.value * 60),
      })) || [],
    title: t(STOP_TIMEOUT_TITLE),
    placeholder: stopTimeoutIsMultiValue ? t("labels.multi") : undefined,
  };

  const numContinuousStopsIsMultiValue = isSymbol(
    numberOfContinuousStopsTillBotStop
  );
  const parsedNumContinuousStops =
    numberOfContinuousStopsTillBotStop && !numContinuousStopsIsMultiValue
      ? parseFloat(numberOfContinuousStopsTillBotStop)
      : 0;
  const hasError = parsedNumContinuousStops > GENERAL_MAX_VALUE;
  const numContinuousStopsSelectorData = {
    shouldDisplay:
      !isGridBot &&
      !isSignalsBot &&
      !isChannelConfiguration &&
      !isTradingViewSignalsBot,
    value: numContinuousStopsIsMultiValue
      ? ""
      : numberOfContinuousStopsTillBotStop,
    onChange: setNumberOfContinuousStopsTillBotStop,
    precision: 0,
    bordered: true,
    title: t("labels.number_of_continuous_stops"),
    placeholder: numContinuousStopsIsMultiValue ? t("labels.multi") : undefined,
    error: hasError
      ? t("messages.errors.value_should_be_less_than", {
          value: GENERAL_MAX_VALUE,
        })
      : undefined,
    helperData: getHelperData("numberOfContinuousStopsTillBotStop"),
  };

  useEffect(() => {
    if (hasError) addError(Error.GENERAL_STOP);
    else removeError(Error.GENERAL_STOP);
  }, [hasError]);

  const isChannelDefaultStop = isSymbol(defaultStop);
  useEffect(() => {
    if (
      !isChannelDefaultStop &&
      checkedFields.has(ToggleField.SKIP_WITHOUT_STOP_LOSS)
    ) {
      toggleField(ToggleField.SKIP_WITHOUT_STOP_LOSS, false);
    }
  }, [isChannelDefaultStop]);

  const skipWithoutStopLossData = {
    title: t(SKIP_SIGNALS_WITHOUT_STOP_TITLE),
    shouldDisplay: isSignalsBot,
    clickWarning: isChannelDefaultStop
      ? ""
      : t("messages.warnings.skip_without_stop_loss_click"),
  };

  return {
    stopTimeoutSelectorData,
    numContinuousStopsSelectorData,
    resetData: tabToRestoreChangesData?.[Steps.STOP],
    getDefaultTrailingStop,
    trailingStopTitle: isLeveraged
      ? t("labels.leveraged_trailing")
      : t("labels.trailing"),
    isLeveraged,
    skipWithoutStopLossData,
  };
};

export default useStop;
